// src/pages/Convocations/ActorSelectionModal.jsx
import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaTimes,
  FaUserTie,
  FaUsers,
  FaCalendarAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../components/Button/Button";
import { getAllActeursListWithGroup } from "./../../services/acteursServices";
import { assignActorToEvent } from "./../../services/acteureventServices";
import { sendNotification } from "../../services/notificationServices";
import { getUserIdByActeurId } from "./../../services/userServices";
import formatDate from "./../../utils/formatDate";

const ActorSelectionModal = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  eventStart,
  refreshList,
}) => {
  const [acteurs, setActeurs] = useState([]);
  const [selectedActeurId, setSelectedActeurId] = useState("");
  const [poste, setPoste] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchActeurs();
      // reset form à l’ouverture
      setSelectedActeurId("");
      setPoste("");
      setFilter("");
    }
  }, [isOpen]);

  const fetchActeurs = async () => {
    try {
      const data = await getAllActeursListWithGroup();
      const sorted = (data || []).sort((a, b) =>
        `${a.nom} ${a.prenom}`
          .toLowerCase()
          .localeCompare(`${b.nom} ${b.prenom}`.toLowerCase())
      );
      setActeurs(sorted);
    } catch (error) {
      console.error("Erreur lors du chargement des acteurs", error);
      toast.error("Erreur lors du chargement des acteurs.");
    }
  };

  const filteredActeurs = acteurs.filter((a) => {
    if (!filter) return true;
    const search = filter.toLowerCase();
    const nomPrenom = `${a.nom} ${a.prenom}`.toLowerCase();
    const club = (a.club_acteur || "").toLowerCase();
    return nomPrenom.includes(search) || club.includes(search);
  });

  const handleAssign = async () => {
    if (!poste) {
      toast.error("Merci de sélectionner un poste.");
      return;
    }
    if (!selectedActeurId) {
      toast.error("Merci de sélectionner un acteur.");
      return;
    }
    try {
      await assignActorToEvent(selectedActeurId, eventId, poste);
      toast.success("Acteur inscrit avec succès.");

      // Récupérer les utilisateurs responsables de l'acteur
      const users = await getUserIdByActeurId(selectedActeurId);

      // Message de notification
      const message = `Le Responsable de la Manifestation ou le Responsable d'arbitrage vous a convoqué sur l'événement : "${eventTitle}" le ${formatDate(
        eventStart
      )} en tant que ${poste}.`;

      for (const user of users) {
        await sendNotification(message, user.user_id);
      }

      refreshList();
      onClose();
    } catch (error) {
      console.error(
        "Erreur lors de l'inscription de l'acteur à l'événement",
        error
      );
      toast.error("Erreur lors de l'inscription de l'acteur à l'événement.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="modal-box bg-white dark:bg-slate-400 p-4 rounded-lg shadow-lg max-w-lg w-full">
        {/* Header */}
        <h3 className="text-xl font-bold uppercase p-3 mb-3 text-center text-white bg-primary rounded-md shadow">
          Inscrire un Acteur
        </h3>

        {/* Rappel événement */}
        <div className="mb-4 rounded-xl bg-slate-50 dark:bg-slate-800/80 p-3 text-xs sm:text-sm text-slate-700 dark:text-slate-100 ring-1 ring-slate-200 dark:ring-slate-700">
          <div className="flex items-start gap-2">
            <FaCalendarAlt className="mt-0.5 text-primary" />
            <div>
              <div className="font-semibold">
                {eventTitle || "Événement non trouvé"}
              </div>
              {eventStart && (
                <div className="mt-0.5">
                  Date :{" "}
                  <span className="font-medium">{formatDate(eventStart)}</span>
                </div>
              )}
              <div className="mt-1 text-[11px] italic text-slate-500 dark:text-slate-300">
                Sélectionnez le poste et l’acteur à convoquer. Une notification
                sera envoyée automatiquement.
              </div>
            </div>
          </div>
        </div>

        {/* Poste */}
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
            Poste sur l’événement
          </label>
          <select
            value={poste}
            onChange={(e) => setPoste(e.target.value)}
            className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          >
            <option value="">Sélectionner un poste</option>
            <option value="Organisateur">Organisateur</option>
            <option value="Responsable Comité">Responsable Comité</option>
            <option value="Bénévolontaire">Bénévolontaire</option>
            <option value="Responsable Arbitrage">Responsable Arbitrage</option>
            <option value="Notateur">Notateur</option>
            <option value="Arbitre">Arbitre</option>
            <option value="Commissaire Sportif">Commissaire Sportif</option>
            <option value="Juge">Juge</option>
          </select>
        </div>

        {/* Filtre acteurs */}
        <div className="mb-2">
          <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
            Filtrer les acteurs (nom, club…)
          </label>
          <input
            type="text"
            className="input input-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
            placeholder="Rechercher un acteur…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {/* Sélection acteur */}
        <div className="mb-4">
          <label className="block text-xs font-semibold mb-1 uppercase tracking-wide">
            Sélectionner un acteur
          </label>
          <select
            value={selectedActeurId}
            onChange={(e) => setSelectedActeurId(e.target.value)}
            className="select select-bordered w-full text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
          >
            <option value="">Sélectionner un acteur</option>
            {filteredActeurs.map((acteur) => (
              <option key={acteur.id} value={acteur.id}>
                {acteur.nom.toUpperCase()} {acteur.prenom}{" "}
                {acteur.club_acteur ? `- ${acteur.club_acteur}` : ""}{" "}
                {(acteur.Groups || []).length > 0
                  ? ` [${acteur.Groups.map((g) => g.nom).join(", ")}]`
                  : ""}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-200 flex items-center gap-1">
            <FaUsers />
            <span>
              {filteredActeurs.length} acteur(s) disponible(s) selon votre
              filtre.
            </span>
          </p>
        </div>

        {/* Boutons */}
        <div className="flex justify-center gap-2 mt-4">
          <Button
            onClick={handleAssign}
            className="btn-primary flex items-center gap-2"
            text="Inscrire"
            icon={FaPlus}
          />
          <Button
            onClick={onClose}
            className="btn-ghost flex items-center gap-2"
            text="Fermer"
            icon={FaTimes}
          />
        </div>
      </div>
    </div>
  );
};

export default ActorSelectionModal;
